// src/redux/slices/generalChatSlice.js

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import io from 'socket.io-client';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../../../config';
import { setBondChatSocket } from './generalChatSingleton';
import {
  loadLocalReadCursor,
  saveLocalReadCursor,
} from '../../utils/bondchat/loungeReadCursorStorage';
import { initializeActivitySocketListeners } from '../activitySocket';

const fetch = (url, options = {}) => {
  let token = null;
  try {
    token = localStorage.getItem('betbond_app_token');
  } catch {}
  return window.fetch(url, {
    ...options,
    headers: {
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
};

const readJson = async (res) => {
  const text = await res.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    const snippet = text.slice(0, 80).replace(/\s+/g, ' ');
    throw new Error(
      res.ok
        ? `Unexpected response from chat API: ${snippet}`
        : `Chat API ${res.status}: ${snippet || res.statusText || 'request failed'}`
    );
  }
};

// =========== SOCKET MANAGEMENT ===========
let socket = null;
let isInitialized = false;
let lastSocketUserId = null;

// Track recently processed messages to prevent duplicates
const recentlyProcessedMessages = new Set();
const PROCESSED_MESSAGE_TTL = 10000; // 10s

const isMessageRecentlyProcessed = (messageKey) => {
  if (recentlyProcessedMessages.has(messageKey)) {
    console.log('[Socket] Message recently processed, skipping:', messageKey);
    return true;
  }
  recentlyProcessedMessages.add(messageKey);
  setTimeout(() => {
    recentlyProcessedMessages.delete(messageKey);
  }, PROCESSED_MESSAGE_TTL);
  return false;
};

// Auto-clear stale typing indicators when stop events are missed
const typingExpiryTimers = new Map();

// Prevent race conditions in message processing
const processingMessages = new Set();
const isMessageBeingProcessed = (messageKey) => {
  if (processingMessages.has(messageKey)) {
    console.log('[Socket] Message currently being processed, skipping:', messageKey);
    return true;
  }
  processingMessages.add(messageKey);
  setTimeout(() => processingMessages.delete(messageKey), 2000);
  return false;
};

// ======== ID helpers (normalize shapes safely) ========
const asString = (v) => (v == null ? null : String(v));
const authorIdOf = (msg) =>
  typeof msg?.userId === 'string'
    ? msg.userId
    : msg?.userId && msg.userId._id
    ? String(msg.userId._id)
    : null;

const logMessageState = (getState, message) => {
  const state = getState();
  const messages = state.generalChat.messages;
  const pendingMessages = state.generalChat.pendingMessages;

  console.log('[Socket] Current message state:', {
    totalMessages: messages.length,
    pendingMessages: pendingMessages.length,
    recentMessageIds: messages.slice(-5).map(m => ({
      id: m._id,
      tempId: m.tempId,
      confirmed: m.confirmed,
      userId: authorIdOf(m)
    })),
    incomingMessage: {
      id: message._id,
      tempId: message.tempId,
      userId: authorIdOf(message)
    }
  });
};

const findMessageInState = (getState, tempId, messageId) => {
  const state = getState();
  const messages = state.generalChat.messages;

  if (tempId) {
    const i = messages.findIndex(m => m.tempId === tempId);
    if (i >= 0) return { message: messages[i], index: i, foundBy: 'tempId' };
  }
  if (messageId) {
    const j = messages.findIndex(m => m._id === messageId);
    if (j >= 0) return { message: messages[j], index: j, foundBy: 'messageId' };
  }
  return null;
};

// Simple socket creation
const createSocket = () => {
  // Always create a new socket to avoid session issues
  if (socket) {
    console.log('[Socket] Disconnecting old socket');
    socket.disconnect();
    socket = null;
  }
  
  console.log('[Socket] Creating new socket instance');
  socket = io(API_BASE_URL, {
    transports: ['websocket'],
    reconnection: true,
    reconnectionAttempts: 8,
    reconnectionDelay: 1500,
    timeout: 30000,
    forceNew: true,
    upgrade: false,
    rememberUpgrade: false,
    withCredentials: false,
    autoConnect: true,
    auth: (cb) => {
      let token = null;
      try { token = localStorage.getItem('betbond_app_token'); } catch {}
      cb(token ? { token } : {});
    },
    query: {
      timestamp: Date.now()
    }
  });
  setBondChatSocket(socket);
  return socket;
};

// =========== INITIAL STATE ===========
const initialState = {
  messages: [],
  unreadCount: 0,
  loungeInForeground: false,
  loading: false,
  error: null,
  currentPage: 0,
  hasMore: true,
  connected: false,
  pendingMessages: [],
  activeUsersCount: 0,
  typingUsers: {},
  lastReadMessageId: null,
  readStateLoaded: false,
  entryLoaded: false,
  hasNewer: false,
  challengeMessages: [],
  challengesHasMore: true,
  challengesLoading: false,
  pollMessages: [],
  pollsHasMore: true,
  pollsLoading: false,
};

// =========== ASYNC THUNKS ===========
export const fetchMessages = createAsyncThunk(
  'generalChat/fetchMessages',
  async ({ page, limit }, { rejectWithValue }) => {
    try {
      const url = `${API_BASE_URL}/api/general-chat/messages?page=${page}&limit=${limit}`;
      const res = await fetch(url);
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Failed to fetch messages');
      }
      const data = await res.json();
      return {
        messages: data.data || [],
        pagination: data.pagination || {}
      };
    } catch (err) {
      return rejectWithValue(err.message || 'Network error occurred');
    }
  }
);

const isPersistableMessageId = (id) => /^[a-f\d]{24}$/i.test(String(id || ''));

export const fetchReadState = createAsyncThunk(
  'generalChat/fetchReadState',
  async ({ userId }) => {
    const rawLocal = await loadLocalReadCursor(userId);
    const localId = isPersistableMessageId(rawLocal) ? rawLocal : null;
    try {
      const res = await fetch(`${API_BASE_URL}/api/general-chat/read-state/${userId}`);
      if (!res.ok) {
        return { lastReadMessageId: localId };
      }
      const json = await res.json();
      const serverId = json?.data?.lastReadMessageId || null;
      const validServer = isPersistableMessageId(serverId) ? serverId : null;
      return { lastReadMessageId: validServer || localId };
    } catch {
      return { lastReadMessageId: localId };
    }
  }
);

export const fetchMessagesBefore = createAsyncThunk(
  'generalChat/fetchMessagesBefore',
  async ({ beforeId, limit = 20 }, { rejectWithValue }) => {
    try {
      const url = `${API_BASE_URL}/api/general-chat/messages/before?beforeId=${encodeURIComponent(
        beforeId
      )}&limit=${limit}`;
      const res = await fetch(url);
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Failed to fetch older messages');
      }
      const data = await res.json();
      return {
        messages: data.data || [],
        hasMore: !!data.hasMore,
      };
    } catch (err) {
      return rejectWithValue(err.message || 'Network error occurred');
    }
  }
);

const loadLegacyChatEntry = async ({ limit, userId, dispatch }) => {
  const [recent, read, unread] = await Promise.all([
    dispatch(fetchRecentMessages({ limit })).unwrap(),
    userId
      ? dispatch(fetchReadState({ userId })).unwrap()
      : Promise.resolve({ lastReadMessageId: null }),
    userId ? dispatch(fetchUnreadCount(userId)).unwrap().catch(() => 0) : 0,
  ]);

  let messages = recent.messages || [];
  const cursor = read?.lastReadMessageId || null;
  let hasOlder = true;
  let pages = 0;

  while (
    cursor &&
    !messages.some((message) => String(message._id) === String(cursor)) &&
    hasOlder &&
    pages < 20
  ) {
    const beforeId = messages[0]?._id;
    if (!beforeId) break;
    const res = await fetch(
      `${API_BASE_URL}/api/general-chat/messages/before?beforeId=${encodeURIComponent(
        beforeId
      )}&limit=50`
    );
    const data = await readJson(res);
    if (!res.ok) throw new Error(data?.message || 'Failed to restore read position');
    const older = data.data || [];
    if (!older.length) {
      hasOlder = false;
      break;
    }
    const existing = new Set(messages.map((message) => String(message._id)));
    messages = [
      ...older.filter((message) => !existing.has(String(message._id))),
      ...messages,
    ];
    hasOlder = !!data.hasMore;
    pages += 1;
  }

  return {
    messages,
    cursor,
    unreadCount: Number(unread) || 0,
    hasOlder,
    hasNewer: false,
  };
};

export const fetchChatEntry = createAsyncThunk(
  'generalChat/fetchChatEntry',
  async ({ limit = 50, context = 10, userId } = {}, { rejectWithValue, dispatch }) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/general-chat/messages/entry?limit=${limit}&context=${context}`
      );
      if (res.status === 404 || res.status === 405) {
        return loadLegacyChatEntry({ limit, userId, dispatch });
      }
      const data = await readJson(res);
      if (!res.ok || !data?.success) throw new Error(data?.message || 'Failed to open chat');
      return {
        messages: data.data || [],
        cursor: data.cursor || null,
        unreadCount: Number(data.unreadCount) || 0,
        hasOlder: !!data.pagination?.hasOlder,
        hasNewer: !!data.pagination?.hasNewer,
      };
    } catch (error) {
      const msg = String(error?.message || '');
      if (
        msg.includes('Unexpected character') ||
        msg.includes('JSON Parse') ||
        msg.includes('Unexpected response') ||
        msg.includes('Chat API 404')
      ) {
        try {
          return await loadLegacyChatEntry({ limit, userId, dispatch });
        } catch (fallbackErr) {
          return rejectWithValue(fallbackErr.message || 'Failed to open chat');
        }
      }
      return rejectWithValue(error.message || 'Failed to open chat');
    }
  }
);

export const fetchMessagesAfter = createAsyncThunk(
  'generalChat/fetchMessagesAfter',
  async ({ afterId, limit = 20 }, { rejectWithValue }) => {
    try {
      if (!afterId) throw new Error('afterId is required');
      const res = await fetch(
        `${API_BASE_URL}/api/general-chat/messages/after?afterId=${encodeURIComponent(
          afterId
        )}&limit=${limit}`
      );
      if (res.status === 404 || res.status === 405) {
        return { messages: [], hasMore: false };
      }
      const data = await readJson(res);
      if (!res.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to fetch newer messages');
      }
      return { messages: data.data || [], hasMore: !!data.hasMore };
    } catch (error) {
      const msg = String(error?.message || '');
      if (
        msg.includes('Unexpected character') ||
        msg.includes('JSON Parse') ||
        msg.includes('Unexpected response') ||
        msg.includes('Chat API 404')
      ) {
        return { messages: [], hasMore: false };
      }
      return rejectWithValue(error.message || 'Failed to fetch newer messages');
    }
  }
);

export const fetchUnreadCount = createAsyncThunk(
  'generalChat/fetchUnreadCount',
  async (userId, { rejectWithValue }) => {
    if (!userId) return 0;
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/general-chat/messages/unread/${userId}?page=0&limit=1`
      );
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to fetch unread count');
      }
      const data = await res.json();
      return data.pagination?.totalUnread ?? 0;
    } catch (err) {
      return rejectWithValue(err.message || 'Network error occurred');
    }
  }
);

export const fetchRecentMessages = createAsyncThunk(
  'generalChat/fetchRecentMessages',
  async ({ limit = 50 }, { rejectWithValue }) => {
    try {
      const url = `${API_BASE_URL}/api/general-chat/messages/recent?limit=${limit}`;
      const res = await fetch(url);
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Failed to fetch recent messages');
      }
      const data = await res.json();
      return {
        messages: data.data || [],
        total: data.total || 0
      };
    } catch (err) {
      return rejectWithValue(err.message || 'Network error occurred');
    }
  }
);

// =========== SLICE ===========
const generalChatSlice = createSlice({
  name: 'generalChat',
  initialState,
  reducers: {
    setConnected(state, { payload }) {
      state.connected = payload;
    },
    setError(state, { payload }) {
      state.error = payload;
    },
    clearError(state) {
      state.error = null;
    },
    setActiveUsersCount(state, { payload }) {
      state.activeUsersCount = payload;
    },
    setLastReadMessageId(state, { payload }) {
      if (payload) state.lastReadMessageId = String(payload);
    },
    setReadStateLoaded(state, { payload }) {
      state.readStateLoaded = !!payload;
    },

    // Message management
    addMessage(state, { payload }) {
      if (!payload || !asString(authorIdOf(payload)) || !payload.userId?.name) {
        console.warn('[Slice] Invalid message payload, skipping:', payload);
        return;
      }

      console.log('[Slice] Adding message to state:', payload._id || payload.tempId);

      const existingIndex = state.messages.findIndex(
        m =>
          (m._id && m._id === payload._id) ||
          (m.tempId && m.tempId === payload.tempId)
      );

      if (existingIndex >= 0) {
        console.log('[Slice] Updating existing message at index:', existingIndex);
        state.messages[existingIndex] = { ...state.messages[existingIndex], ...payload };
      } else {
        console.log('[Slice] Adding new message to end');
        state.messages.push(payload);
      }

      console.log('[Slice] Total messages in state:', state.messages.length);
    },

    addLocalMessage(state, { payload }) {
      console.log('[Slice] Adding local message:', payload.tempId, 'to state with', state.messages.length, 'existing messages');

      const existingIndex = state.messages.findIndex(m => m.tempId === payload.tempId);
      if (existingIndex >= 0) {
        state.messages[existingIndex] = payload;
      } else {
        state.messages.push(payload);
      }

      if (payload.tempId && !state.pendingMessages.includes(payload.tempId)) {
        state.pendingMessages.push(payload.tempId);
      }
      if (payload.tempId) {
        state.lastReadMessageId = payload.tempId;
      }
    },

    confirmMessage(state, { payload }) {
      console.log('[Slice] Attempting to confirm message:', payload.tempId, 'in state with', state.messages.length, 'messages');

      const msgIndex = state.messages.findIndex(m => m.tempId === payload.tempId);

      if (msgIndex >= 0) {
        const currentMessage = state.messages[msgIndex];
        state.messages[msgIndex] = {
          ...currentMessage,
          _id: payload._id || currentMessage._id,
          confirmed: true,
          userId: {
            _id: payload.userId?._id || currentMessage.userId._id,
            name: payload.userId?.name || currentMessage.userId.name
          },
          message: payload.message ?? currentMessage.message,
          fixtureRefs: payload.fixtureRefs ?? currentMessage.fixtureRefs,
          replyTo: payload.replyTo ?? currentMessage.replyTo,
          reactions: payload.reactions ?? currentMessage.reactions,
          poll: payload.poll ?? currentMessage.poll,
          chatChallenge: payload.chatChallenge ?? currentMessage.chatChallenge,
          createdAt: payload.createdAt || currentMessage.createdAt,
          readBy: payload.readBy ?? currentMessage.readBy,
          likes: payload.likes ?? currentMessage.likes
        };
        state.pendingMessages = state.pendingMessages.filter(id => id !== payload.tempId);
        if (payload._id) {
          state.lastReadMessageId = String(payload._id);
        }
      } else {
        console.warn('[Slice] Message not found for confirmation:', payload.tempId);
        if (payload._id && payload.userId?._id && payload.userId?.name) {
          state.messages.push({ ...payload, confirmed: true });
        }
      }
    },

    updateMessageLikes(state, { payload }) {
      const { messageId, likes } = payload;
      const i = state.messages.findIndex(m => m._id === messageId || m.tempId === messageId);
      if (i >= 0) state.messages[i].likes = likes || [];
    },

    updateMessageReactions(state, { payload }) {
      const { messageId, reactions } = payload;
      const i = state.messages.findIndex(m => m._id === messageId || m.tempId === messageId);
      if (i >= 0) state.messages[i].reactions = reactions || [];
    },

    setTypingUser(state, { payload }) {
      const { userId, name, isTyping } = payload || {};
      if (!userId) return;
      if (!state.typingUsers) state.typingUsers = {};
      if (isTyping) {
        state.typingUsers[String(userId)] = name || 'Someone';
      } else {
        delete state.typingUsers[String(userId)];
      }
    },

    updatePollResults(state, { payload }) {
      const { messageId, yesVotes, noVotes } = payload;
      const apply = (list) => {
        if (!Array.isArray(list)) return;
        const i = list.findIndex(
          (m) => String(m._id) === String(messageId) || String(m.tempId) === String(messageId)
        );
        if (i >= 0 && list[i].poll) {
          list[i].poll = {
            ...list[i].poll,
            yesVotes: yesVotes || [],
            noVotes: noVotes || [],
          };
        }
      };
      apply(state.messages);
      apply(state.pollMessages);
    },

    updateChatChallenge(state, { payload }) {
      const { messageId, chatChallenge } = payload || {};
      if (!messageId || !chatChallenge) return;
      const apply = (list) => {
        if (!Array.isArray(list)) return;
        const i = list.findIndex(
          (m) => String(m._id) === String(messageId) || String(m.tempId) === String(messageId)
        );
        if (i >= 0) list[i].chatChallenge = chatChallenge;
      };
      apply(state.messages);
      apply(state.challengeMessages);
    },

    // Mark a message as reported in local state
    updateMessageReports(state, { payload }) {
      const { messageId, reportReason } = payload || {};
      const i = state.messages.findIndex(m => m._id === messageId || m.tempId === messageId);
      if (i >= 0) {
        state.messages[i] = {
          ...state.messages[i],
          isReported: true,
          reportReason: reportReason || ''
        };
      }
    },

    // >>> Hardened reducer <<<
    markMessagesAsRead(state, { payload }) {
      const { messageIds, userId } = payload;
      const me = asString(userId);
      if (!me || !Array.isArray(messageIds) || !messageIds.length) return;

      const idSet = new Set(messageIds.map(String));

      state.messages = state.messages.map((msg) => {
        if (!msg._id || !idSet.has(String(msg._id))) return msg;
        const existing = Array.isArray(msg.readBy) ? msg.readBy : [];
        if (existing.map(String).includes(me)) return msg;
        return { ...msg, readBy: [...existing, userId] };
      });
    },

    updateUnreadCount(state, { payload }) {
      const n = Number(payload);
      state.unreadCount = Number.isFinite(n) && n >= 0 ? n : 0;
    },
    adjustUnreadCount(state, { payload }) {
      const delta = Number(payload) || 0;
      const next = state.unreadCount + delta;
      state.unreadCount = next < 0 ? 0 : next;
    },
    setLoungeInForeground(state, { payload }) {
      state.loungeInForeground = !!payload;
    },

    clearMessages(state) {
      state.messages = [];
      state.currentPage = 0;
      state.hasMore = true;
      state.pendingMessages = [];
    },
    setMessages(state, { payload }) {
      state.messages = Array.isArray(payload) ? payload : [];
    },
    removeMessage(state, { payload }) {
      const id = payload?.id;
      if (!id) return;
      const keep = (m) => String(m._id || m.tempId) !== String(id);
      state.messages = state.messages.filter(keep);
      state.challengeMessages = (state.challengeMessages || []).filter(keep);
      state.pollMessages = (state.pollMessages || []).filter(keep);
    }
  },

  extraReducers: builder => {
    builder
      .addCase(fetchMessages.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMessages.fulfilled, (state, { payload }) => {
        state.loading = false;

        const page = payload.pagination?.currentPage || 0;
        const loadedCount = Array.isArray(payload.messages) ? payload.messages.length : 0;

        if (page === 0) {
          state.messages = payload.messages || [];
          state.currentPage = 0;
          // If backend doesn't send hasMore, infer from count
          state.hasMore = typeof payload.pagination?.hasMore === 'boolean'
            ? payload.pagination.hasMore
            : loadedCount > 0;
          return;
        }

        // For older pages, prepend only truly new items
        const existingIds = new Set(state.messages.map(m => m._id).filter(Boolean));
        const newMessages = (payload.messages || []).filter(m => m._id && !existingIds.has(m._id));
        const dedupedCount = newMessages.length;
        if (dedupedCount > 0) {
          state.messages = [...newMessages, ...state.messages];
        }

        state.currentPage = page;

        // Hard stop further loads when server returns empty or only duplicates
        if (loadedCount === 0 || dedupedCount === 0) {
          state.hasMore = false;
        } else {
          state.hasMore = typeof payload.pagination?.hasMore === 'boolean'
            ? payload.pagination.hasMore
            : dedupedCount > 0;
        }
      })
      .addCase(fetchMessages.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      .addCase(fetchMessagesBefore.fulfilled, (state, { payload }) => {
        const existingIds = new Set(state.messages.map((m) => m._id).filter(Boolean));
        const newMessages = (payload.messages || []).filter(
          (m) => m._id && !existingIds.has(m._id)
        );
        if (newMessages.length > 0) {
          state.messages = [...newMessages, ...state.messages];
        }
        // Stop paginating when nothing new arrives (avoids infinite fetch loops).
        if (newMessages.length === 0) {
          state.hasMore = false;
        } else if (typeof payload.hasMore === 'boolean') {
          state.hasMore = payload.hasMore;
        }
      })
      .addCase(fetchRecentMessages.fulfilled, (state, { payload }) => {
        state.messages = payload.messages || [];
        state.currentPage = 0;
        state.hasMore = true;
      })
      .addCase(fetchRecentMessages.rejected, (state, { payload }) => {
        state.error = payload;
      })
      .addCase(fetchChatEntry.pending, (state) => {
        state.loading = true;
        state.entryLoaded = false;
        state.error = null;
      })
      .addCase(fetchChatEntry.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.entryLoaded = true;
        state.readStateLoaded = true;
        state.messages = payload.messages || [];
        state.lastReadMessageId = payload.cursor || null;
        state.unreadCount = payload.unreadCount || 0;
        state.hasMore = !!payload.hasOlder;
        state.hasNewer = !!payload.hasNewer;
      })
      .addCase(fetchChatEntry.rejected, (state, { payload }) => {
        state.loading = false;
        state.entryLoaded = true;
        state.error = payload;
      })
      .addCase(fetchMessagesAfter.fulfilled, (state, { payload }) => {
        const existing = new Set(
          state.messages.map((message) => String(message._id || message.tempId))
        );
        const newer = (payload.messages || []).filter(
          (message) => !existing.has(String(message._id || message.tempId))
        );
        state.messages = [...state.messages, ...newer];
        state.hasNewer = !!payload.hasMore;
      })
      
      .addCase(fetchChatChallenges.pending, (state) => {
        state.challengesLoading = true;
      })
      .addCase(fetchChatChallenges.fulfilled, (state, { payload }) => {
        state.challengesLoading = false;
        state.challengeMessages = payload?.messages || [];
        state.challengesHasMore = !!payload?.hasMore;
      })
      .addCase(fetchChatChallenges.rejected, (state, { payload }) => {
        state.challengesLoading = false;
        state.error = payload || 'Failed to fetch challenges';
      })
      .addCase(fetchChatChallengesBefore.fulfilled, (state, { payload }) => {
        const incoming = payload?.messages || [];
        if (!incoming.length) {
          state.challengesHasMore = false;
          return;
        }
        const existingIds = new Set(
          state.challengeMessages.map((m) => String(m._id || m.tempId))
        );
        const older = incoming.filter(
          (m) => !existingIds.has(String(m._id || m.tempId))
        );
        state.challengeMessages = [...older, ...state.challengeMessages];
        state.challengesHasMore = !!payload?.hasMore;
      })
      .addCase(fetchChatPolls.pending, (state) => {
        state.pollsLoading = true;
      })
      .addCase(fetchChatPolls.fulfilled, (state, { payload }) => {
        state.pollsLoading = false;
        state.pollMessages = payload?.messages || [];
        state.pollsHasMore = !!payload?.hasMore;
      })
      .addCase(fetchChatPolls.rejected, (state, { payload }) => {
        state.pollsLoading = false;
        state.error = payload || 'Failed to fetch polls';
      })
      .addCase(fetchChatPollsBefore.fulfilled, (state, { payload }) => {
        const incoming = payload?.messages || [];
        if (!incoming.length) {
          state.pollsHasMore = false;
          return;
        }
        const existingIds = new Set(
          state.pollMessages.map((m) => String(m._id || m.tempId))
        );
        const older = incoming.filter(
          (m) => !existingIds.has(String(m._id || m.tempId))
        );
        state.pollMessages = [...older, ...state.pollMessages];
        state.pollsHasMore = !!payload?.hasMore;
      })
.addCase(fetchReadState.fulfilled, (state, { payload }) => {
        state.readStateLoaded = true;
        if (payload?.lastReadMessageId) {
          state.lastReadMessageId = String(payload.lastReadMessageId);
        }
      })
      .addCase(fetchReadState.rejected, (state) => {
        state.readStateLoaded = true;
      })
      .addCase(fetchUnreadCount.fulfilled, (state, { payload }) => {
        state.unreadCount = typeof payload === 'number' ? payload : 0;
      });
  }
});

// =========== SOCKET HANDLERS SETUP ===========
const maybeIncrementUnread = (dispatch, getState, userId, message) => {
  if (!userId || !message) return;
  if (asString(authorIdOf(message)) === asString(userId)) return;
  if (getState().generalChat.loungeInForeground) return;
  const readBy = Array.isArray(message.readBy) ? message.readBy.map(String) : [];
  if (readBy.includes(String(userId))) return;
  dispatch(adjustUnreadCount(1));
};

const setupSocketHandlers = (s, dispatch, getState, userId) => {
  // Prevent stacking duplicate handlers on hot reload / remounts
  s.removeAllListeners();

  // Connection events
  s.on('connect', () => {
    console.log('[Socket] Connected:', s.id);
    console.log('[Socket] Using transport:', s.io.engine.transport.name);
    console.log('[Socket] Available upgrades:', s.io.engine.transport.supportsBinary ? 'binary' : 'text');
    dispatch(setConnected(true));
    dispatch(clearError());
    // Register user to receive targeted unread updates
    if (userId) {
      try { s.emit('registerUser', { userId }); } catch {}
      dispatch(fetchUnreadCount(userId));
    }
    s.emit('getUnreadCount', { userId });
  });

  s.on('disconnect', () => {
    console.log('[Socket] Disconnected');
    dispatch(setConnected(false));
  });

  s.on('connect_error', (error) => {
    console.error('[Socket] Connection error:', error);
    console.error('[Socket] Error details:', {
      message: error.message,
      type: error.type,
      description: error.description,
      transport: error.transport,
      context: error.context
    });
    
    // Additional debugging for React Native
    if (error.context && error.context.status) {
      console.error('[Socket] HTTP Status:', error.context.status);
      console.error('[Socket] Response:', error.context.responseText);
    }
    
    // Handle session ID unknown error specifically
    if (error.message === 'Session ID unknown' || (error.context && error.context.responseText && error.context.responseText.includes('Session ID unknown'))) {
      console.log('[Socket] Session ID unknown - attempting to reconnect with fresh connection...');
      // Disconnect and try again after a short delay
      setTimeout(() => {
        if (socket) {
          socket.disconnect();
          socket = null;
        }
        // Try to reconnect
        const newSocket = createSocket();
        // Re-setup the socket handlers
        setupSocketHandlers(newSocket, dispatch, getState, userId);
      }, 2000);
      return;
    }
    
    dispatch(setConnected(false));
    dispatch(setError('Connection failed. Using fallback transport...'));
  });

  s.on('error', (error) => {
    console.error('[Socket] Socket error:', error);
    console.error('[Socket] Socket error details:', {
      message: error.message,
      stack: error.stack,
      transport: s.io.engine?.transport?.name
    });
    dispatch(setError(error.message || 'Socket error occurred'));
  });

  s.on('reconnect', () => {
    console.log('[Socket] Reconnected');
    dispatch(setConnected(true));
    dispatch(clearError());
  });

  // Message events
  s.on('newMessage', (message) => {
    console.log('[Socket] New message received:', message._id, 'tempId:', message.tempId, 'userId:', message.userId);

    if (!message || typeof message !== 'object') {
      console.warn('[Socket] Invalid message received:', message);
      return;
    }

    const messageKey = message._id || message.tempId;
    if (!messageKey) {
      console.warn('[Socket] Message has no ID or tempId, skipping:', message);
      return;
    }

    logMessageState(getState, message);

    if (isMessageRecentlyProcessed(messageKey)) return;
    if (isMessageBeingProcessed(messageKey)) return;

    console.log('[Socket] Processing message:', {
      messageKey,
      timestamp: new Date().toISOString(),
      socketId: s.id,
      isConnected: s.connected
    });

    if (message.tempId && asString(authorIdOf(message)) === asString(userId)) {
      const found = findMessageInState(getState, message.tempId, message._id);
      if (found) {
        if (found.message.confirmed) return;
        console.log('[Socket] Confirming local message:', message.tempId, 'found by:', found.foundBy);
        dispatch(confirmMessage(message));
        if (message._id) saveLocalReadCursor(userId, message._id);
      } else {
        // This can happen if the UI refreshed/reloaded messages and dropped the local pending temp entry
        // before the server confirmation arrived. In that case, just upsert the confirmed message.
        console.log('[Socket] Local temp message missing; upserting confirmed message:', message.tempId);
        if (message._id && message.userId?._id && message.userId?.name) {
          dispatch(addMessage({ ...message, confirmed: true }));
        }
      }
    } else if (message.userId?._id && message.userId?.name) {
      const found = findMessageInState(getState, message.tempId, message._id);
      if (found) return;
      console.log('[Socket] Adding new message from other user');
      maybeIncrementUnread(dispatch, getState, userId, message);
      dispatch(addMessage(message));
    } else {
      console.warn('[Socket] Malformed message received, skipping:', message);
    }
  });

  s.on('messageLiked', (update) => dispatch(updateMessageLikes(update)));
  s.on('messageReactions', (update) => dispatch(updateMessageReactions(update)));
  s.on('typingUpdate', ({ userId: typingUserId, name, isTyping }) => {
    if (!typingUserId || String(typingUserId) === String(userId)) return;
    const key = String(typingUserId);
    if (typingExpiryTimers.has(key)) {
      clearTimeout(typingExpiryTimers.get(key));
      typingExpiryTimers.delete(key);
    }
    dispatch(setTypingUser({ userId: key, name, isTyping }));
    if (isTyping) {
      typingExpiryTimers.set(
        key,
        setTimeout(() => {
          typingExpiryTimers.delete(key);
          dispatch(setTypingUser({ userId: key, isTyping: false }));
        }, 5000)
      );
    }
  });
  s.on('pollCreated', (pollMessage) => {
    if (pollMessage.tempId && asString(authorIdOf(pollMessage)) === asString(userId)) {
      dispatch(confirmMessage(pollMessage));
        if (pollMessage._id) saveLocalReadCursor(userId, pollMessage._id);
    } else {
      maybeIncrementUnread(dispatch, getState, userId, pollMessage);
      dispatch(addMessage(pollMessage));
    }
  });
  s.on('pollVoted', (update) => dispatch(updatePollResults(update)));
  s.on('chatChallengeUpdated', (update) => dispatch(updateChatChallenge(update)));
  s.on('chatChallengeCreated', (response) => {
    toast.success(
      response?.description
        ? `${response?.message || 'Challenge dropped'}: ${response.description}`
        : response?.message || 'Challenge dropped'
    );
  });
  s.on('chatChallengeJoined', (response) => {
    if (response?.messageId && response?.chatChallenge) {
      dispatch(
        updateChatChallenge({
          messageId: response.messageId,
          chatChallenge: response.chatChallenge,
        })
      );
    }
    toast.success(
      response?.description
        ? `${response?.message || 'Challenge accepted!'}: ${response.description}`
        : response?.message || 'Challenge accepted!'
    );
  });
  s.on('chatChallengeError', (error) => {
    const trimmed = String(error?.message || '').trim();
    if (error?.tempId) {
      dispatch(removeMessage({ id: error.tempId }));
    }
    if (!trimmed) return;
    toast.error(trimmed);
    dispatch(setError(trimmed));
  });
  s.on('messageReported', (update) => dispatch(updateMessageReports(update)));
  s.on('unreadCountUpdate', ({ userId: uid, unreadCount }) => {
    if (uid && userId && String(uid) !== String(userId)) return;
    dispatch(updateUnreadCount(unreadCount));
  });
  s.on('messagesMarkedAsRead', ({ messageIds, userId }) => {
    if (Array.isArray(messageIds) && userId) {
      dispatch(markMessagesAsRead({ messageIds, userId }));
    }
  });

  // Active users count listener
  s.on('activeUsersCount', ({ count }) => {
    console.log('[Socket] Active users count received:', count);
    dispatch(setActiveUsersCount(count));
  });

  initializeActivitySocketListeners(dispatch, s);
};

// =========== SOCKET ACTIONS ===========
/** Connect BondChat socket and refresh unread badge (call on login / app start). */

const challengeListQuery = ({ limit, beforeId, mine } = {}) => {
  const params = new URLSearchParams({ limit: String(limit) });
  if (beforeId) params.set('beforeId', String(beforeId));
  if (mine && mine !== 'all') params.set('mine', String(mine));
  return params.toString();
};

export const fetchChatChallenges = createAsyncThunk(
  'generalChat/fetchChatChallenges',
  async ({ limit = 30, mine = 'all' } = {}, { rejectWithValue }) => {
    try {
      const url = `${API_BASE_URL}/api/general-chat/challenges?${challengeListQuery({
        limit,
        mine,
      })}`;
      const res = await fetch(url);
      const data = await res.json();
      if (!res.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to fetch challenges');
      }
      return {
        messages: data.data || [],
        hasMore: !!data?.pagination?.hasMore,
        nextBeforeId: data?.pagination?.nextBeforeId || null,
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch challenges');
    }
  }
);

export const fetchChatChallengesBefore = createAsyncThunk(
  'generalChat/fetchChatChallengesBefore',
  async ({ beforeId, limit = 20, mine = 'all' } = {}, { rejectWithValue }) => {
    try {
      if (!beforeId) throw new Error('beforeId is required');
      const url = `${API_BASE_URL}/api/general-chat/challenges?${challengeListQuery({
        limit,
        beforeId,
        mine,
      })}`;
      const res = await fetch(url);
      const data = await res.json();
      if (!res.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to fetch older challenges');
      }
      return {
        messages: data.data || [],
        hasMore: !!data?.pagination?.hasMore,
        nextBeforeId: data?.pagination?.nextBeforeId || null,
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch older challenges');
    }
  }
);

export const fetchChatPolls = createAsyncThunk(
  'generalChat/fetchChatPolls',
  async ({ limit = 30 } = {}, { rejectWithValue }) => {
    try {
      const url = `${API_BASE_URL}/api/general-chat/polls?limit=${limit}`;
      const res = await fetch(url);
      const data = await res.json();
      if (!res.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to fetch polls');
      }
      return {
        messages: data.data || [],
        hasMore: !!data?.pagination?.hasMore,
        nextBeforeId: data?.pagination?.nextBeforeId || null,
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch polls');
    }
  }
);

export const fetchChatPollsBefore = createAsyncThunk(
  'generalChat/fetchChatPollsBefore',
  async ({ beforeId, limit = 20 } = {}, { rejectWithValue }) => {
    try {
      if (!beforeId) throw new Error('beforeId is required');
      const url = `${API_BASE_URL}/api/general-chat/polls?limit=${limit}&beforeId=${encodeURIComponent(
        beforeId
      )}`;
      const res = await fetch(url);
      const data = await res.json();
      if (!res.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to fetch older polls');
      }
      return {
        messages: data.data || [],
        hasMore: !!data?.pagination?.hasMore,
        nextBeforeId: data?.pagination?.nextBeforeId || null,
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch older polls');
    }
  }
);

export const syncBondChatForUser = (userId) => (dispatch) => {
  if (!userId) return;
  dispatch(fetchUnreadCount(userId));
  dispatch(fetchReadState({ userId }));
  dispatch(initializeSocket(userId));
};

export const initializeSocket = (userId) => (dispatch, getState) => {
  if (lastSocketUserId && userId && lastSocketUserId !== userId) {
    console.log('[Socket] User changed, reinitializing');
    try {
      socket?.removeAllListeners();
      socket?.disconnect();
    } catch {}
    socket = null;
    setBondChatSocket(null);
    isInitialized = false;
  }

  if (isInitialized && socket && socket.connected && lastSocketUserId === userId) {
    console.log('[Socket] Already initialized');
    if (userId) {
      try {
        socket.emit('registerUser', { userId });
        socket.emit('getUnreadCount', { userId });
      } catch {}
    }
    return;
  }

  if (isInitialized && socket && !socket.connected) {
    console.log('[Socket] Reinitializing disconnected socket');
    try {
      socket.removeAllListeners();
      socket.disconnect();
    } catch {}
    socket = null;
    setBondChatSocket(null);
    isInitialized = false;
  }

  console.log('[Socket] Initializing for user:', userId);
  const s = createSocket();
  
  // Setup all socket handlers
  setupSocketHandlers(s, dispatch, getState, userId);

  lastSocketUserId = userId;
  isInitialized = true;
};

export const disconnectSocket = () => () => {
  console.log('[Socket] Disconnecting...');
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }
  setBondChatSocket(null);
  isInitialized = false;
  lastSocketUserId = null;
  recentlyProcessedMessages.clear();
  processingMessages.clear();
};

export const sendMessage = ({ userId, message, tempId, fixtureRefs = [], replyTo = null }) => (dispatch, getState) => {
  const user = getState().appAuth.user;
  const fixtureIds = (fixtureRefs || [])
    .map((r) => r?.fixtureId?.toString?.() || (typeof r?.fixtureId === 'string' ? r.fixtureId : null))
    .filter(Boolean);

  const localMessage = {
    tempId,
    userId: { _id: userId, name: user?.name || 'You' },
    message,
    fixtureRefs: fixtureRefs || [],
    replyTo: replyTo || undefined,
    createdAt: new Date().toISOString(),
    likes: [],
    readBy: [userId],
    confirmed: false
  };

  dispatch(addLocalMessage(localMessage));

  const payload = { userId, message, tempId, fixtureIds };
  if (replyTo?.messageId) payload.replyToMessageId = String(replyTo.messageId);
  if (socket && socket.connected) {
    socket.emit('sendMessage', payload);
  } else if (socket) {
    socket.once('connect', () => socket.emit('sendMessage', payload));
    try {
      socket.connect();
    } catch {}
  }
};

export const reactToMessage = ({ messageId, userId, emoji }) => (dispatch, getState) => {
  const state = getState();
  const msg = state.generalChat.messages.find(
    (m) => m._id === messageId || m.tempId === messageId
  );
  if (msg) {
    const reactions = (msg.reactions || []).map((r) => ({
      emoji: r.emoji,
      users: [...(r.users || [])].map(String),
    }));
    const entry = reactions.find((r) => r.emoji === emoji);
    if (!entry) {
      reactions.push({ emoji, users: [String(userId)] });
    } else if (entry.users.includes(String(userId))) {
      entry.users = entry.users.filter((id) => id !== String(userId));
    } else {
      entry.users.push(String(userId));
    }
    dispatch(updateMessageReactions({
      messageId,
      reactions: reactions.filter((r) => r.users.length > 0),
    }));
  }

  const payload = { messageId, userId, emoji };
  if (socket && socket.connected) {
    socket.emit('reactMessage', payload);
  } else if (socket) {
    socket.once('connect', () => socket.emit('reactMessage', payload));
    try {
      socket.connect();
    } catch {}
  }
};

export const emitLoungeTyping = ({ userId, name, isTyping }) => () => {
  if (!userId || !socket || !socket.connected) return;
  try {
    socket.emit('typing', { userId, name, isTyping });
  } catch {}
};

export const likeMessage = ({ messageId, userId }) => (dispatch, getState) => {
  // Optimistic update - immediately update the UI
  const state = getState();
  const messageIndex = state.generalChat.messages.findIndex(m => m._id === messageId || m.tempId === messageId);
  
  if (messageIndex >= 0) {
    const message = state.generalChat.messages[messageIndex];
    const isCurrentlyLiked = message.likes && message.likes.includes(userId);
    
    // Create updated likes array
    let updatedLikes = [...(message.likes || [])];
    if (isCurrentlyLiked) {
      updatedLikes = updatedLikes.filter(id => id !== userId);
    } else {
      updatedLikes.push(userId);
    }
    
    // Dispatch optimistic update
    dispatch(updateMessageLikes({ messageId, likes: updatedLikes }));
  }
  
  // Send to server
  if (socket && socket.connected) {
    socket.emit('likeMessage', { messageId, userId });
  } else if (socket) {
    socket.once('connect', () => socket.emit('likeMessage', { messageId, userId }));
    try {
      socket.connect();
    } catch {}
  }
};

export const createPollMessage = ({ userId, question, tempId, fixtureRefs = [] }) => (dispatch, getState) => {
  const user = getState().appAuth.user;
  const fixtureIds = (fixtureRefs || [])
    .map((r) => r?.fixtureId?.toString?.() || (typeof r?.fixtureId === 'string' ? r.fixtureId : null))
    .filter(Boolean);

  const localPoll = {
    tempId,
    userId: { _id: userId, name: user?.name || 'You' },
    poll: { question, yesVotes: [], noVotes: [] },
    fixtureRefs: fixtureRefs || [],
    createdAt: new Date().toISOString(),
    readBy: [userId],
    confirmed: false
  };

  dispatch(addLocalMessage(localPoll));

  const payload = { userId, question, tempId, fixtureIds };
  if (socket && socket.connected) {
    socket.emit('createPoll', payload);
  } else if (socket) {
    socket.once('connect', () => socket.emit('createPoll', payload));
    try {
      socket.connect();
    } catch {}
  }
};

const CHALLENGE_ACK_TIMEOUT_MS = 45000;

const waitForChatChallengeAck = ({
  successEvent,
  matchSuccess,
  matchError,
  emit,
  timeoutMs = CHALLENGE_ACK_TIMEOUT_MS,
}) =>
  new Promise((resolve, reject) => {
    if (!socket) {
      reject(new Error('Not connected. Try again.'));
      return;
    }

    let settled = false;

    const cleanup = () => {
      socket.off(successEvent, onSuccess);
      socket.off('chatChallengeError', onError);
    };

    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error('Request timed out. Check your connection and try again.'));
    }, timeoutMs);

    const onSuccess = (response) => {
      if (!matchSuccess(response) || settled) return;
      settled = true;
      clearTimeout(timer);
      cleanup();
      resolve(response);
    };

    const onError = (error) => {
      if (!matchError(error) || settled) return;
      settled = true;
      clearTimeout(timer);
      cleanup();
      const err = new Error(
        String(error?.message || 'Challenge request failed').trim() ||
          'Challenge request failed'
      );
      err.silent = true;
      reject(err);
    };

    socket.on(successEvent, onSuccess);
    socket.on('chatChallengeError', onError);

    try {
      emit();
    } catch (error) {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      cleanup();
      reject(error instanceof Error ? error : new Error('Challenge request failed'));
    }
  });

export const createChatChallenge = ({
  userId,
  tempId,
  fixtureId,
  agreedOdds,
  stance = 'for',
  creatorBudget,
  offeredOdds,
  amount,
  minStake,
  maxPeopleAllowed,
  message,
  fixtureRef,
  pickLabel,
}) => (dispatch, getState) => {
  const user = getState().appAuth.user;
  const stakeFloor = Number(minStake ?? amount) || 500;
  const normalizedStance = stance === 'against' ? 'against' : 'for';
  dispatch(addLocalMessage({
    tempId,
    userId: { _id: userId, name: user?.name || 'You' },
    message: message || `⚔️ Challenge: ${pickLabel}`,
    chatChallenge: {
      p2pBetId: null,
      fixtureRef,
      agreedOdds,
      stance: normalizedStance,
      pickLabel,
      amount: stakeFloor,
      minStake: stakeFloor,
      creatorBudget: creatorBudget ?? amount,
      offeredOdds: offeredOdds ?? 2,
      maxPeopleAllowed: maxPeopleAllowed ?? 0,
      joinedPeople: 0,
      remainingBudget: creatorBudget ?? amount,
      remainingCapacity: creatorBudget ?? amount,
      status: 'open',
      recentCounters: [],
    },
    createdAt: new Date().toISOString(),
    readBy: [userId],
    confirmed: false,
  }));

  const payload = {
    userId,
    tempId,
    fixtureId,
    agreedOdds,
    stance: normalizedStance,
    creatorBudget,
    offeredOdds,
    amount: stakeFloor,
    minStake: stakeFloor,
    message,
    idempotencyKey: tempId,
  };

  return waitForChatChallengeAck({
    successEvent: 'chatChallengeCreated',
    matchSuccess: (response) =>
      String(response?.data?.tempId || '') === String(tempId),
    matchError: (error) =>
      error?.action === 'create' &&
      String(error?.tempId || '') === String(tempId),
    emit: () => {
      if (socket && socket.connected) {
        socket.emit('createChatChallenge', payload);
      } else if (socket) {
        socket.once('connect', () => socket.emit('createChatChallenge', payload));
        try { socket.connect(); } catch {}
      } else {
        throw new Error('Not connected. Try again.');
      }
    },
  }).catch((error) => {
    dispatch(removeMessage({ id: tempId }));
    if (!error?.silent) {
      toast.error(error?.message || 'Could not post challenge');
    }
    throw error;
  });
};

export const joinChatChallenge = ({ userId, messageId, idempotencyKey, stake }) => () => {
  const payload = {
    userId,
    messageId,
    idempotencyKey,
    stake: Number(stake) || 500,
  };
  return waitForChatChallengeAck({
    successEvent: 'chatChallengeJoined',
    matchSuccess: (response) =>
      String(response?.messageId || '') === String(messageId),
    matchError: (error) =>
      error?.action === 'join' &&
      String(error?.messageId || '') === String(messageId),
    emit: () => {
      if (socket && socket.connected) {
        socket.emit('joinChatChallenge', payload);
      } else if (socket) {
        socket.once('connect', () => socket.emit('joinChatChallenge', payload));
        try { socket.connect(); } catch {}
      } else {
        throw new Error('Not connected. Try again.');
      }
    },
  }).catch((error) => {
    if (!error?.silent) {
      toast.error(error?.message || 'Could not accept challenge');
    }
    throw error;
  });
};

export const votePollMessage = ({ messageId, userId, vote }) => () => {
  if (socket && socket.connected) {
    socket.emit('votePoll', { messageId, userId, vote });
  } else if (socket) {
    socket.once('connect', () => socket.emit('votePoll', { messageId, userId, vote }));
    try {
      socket.connect();
    } catch {}
  }
};

export const markAsReadMessage =
  ({ messageIds, userId, lastReadMessageId }) =>
  (dispatch) => {
    const validIds = [
      ...new Set(
        (messageIds || [])
          .map((id) => (id == null ? null : String(id)))
          .filter((id) => isPersistableMessageId(id))
      ),
    ];
    if (!validIds.length || !userId) return;

    const cursor =
      lastReadMessageId && isPersistableMessageId(lastReadMessageId)
        ? String(lastReadMessageId)
        : validIds[validIds.length - 1];

    dispatch(markMessagesAsRead({ messageIds: validIds, userId }));

    if (cursor) {
      dispatch(setLastReadMessageId(String(cursor)));
      saveLocalReadCursor(userId, cursor);
    }
    const payload = { messageIds: validIds, userId, lastReadMessageId: cursor };
    if (socket && socket.connected) {
      socket.emit('markMessagesAsRead', payload);
    } else if (socket) {
      socket.once('connect', () => socket.emit('markMessagesAsRead', payload));
      try {
        socket.connect();
      } catch {}
    }
  };

export const persistReadCursor =
  ({ userId, messageId }) =>
  async (dispatch) => {
    if (!userId || !messageId) return;
    const id = String(messageId);
    dispatch(setLastReadMessageId(id));
    if (!isPersistableMessageId(id)) return;
    await saveLocalReadCursor(userId, id);
    if (socket && socket.connected) {
      socket.emit('updateReadCursor', { userId, lastReadMessageId: id });
    } else {
      try {
        await fetch(`${API_BASE_URL}/api/general-chat/read-state/${userId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ lastReadMessageId: id }),
        });
      } catch {
        // ignore
      }
    }
  };

export const reportMessageAction = ({ messageId, userId, reason }) => () => {
  if (socket && socket.connected) {
    socket.emit('reportMessage', { messageId, userId, reason });
  } else if (socket) {
    socket.once('connect', () =>
      socket.emit('reportMessage', { messageId, userId, reason })
    );
    try {
      socket.connect();
    } catch {}
  }
};

// Delete a message (author-only); will also remove any attached poll since it is part of the same doc
export const deleteMessageAction = ({ messageId, userId }) => async (dispatch, getState) => {
  try {
    // Optimistic remove
    dispatch(removeMessage({ id: messageId }));

    const res = await fetch(`${API_BASE_URL}/api/general-chat/messages/${messageId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId })
    });
    const data = await res.json();
    if (!res.ok || !data?.success) {
      throw new Error(data?.message || 'Failed to delete message');
    }
  } catch (e) {
    dispatch(setError(e.message || 'Failed to delete message'));
    // Reload to restore list if optimistic removal was wrong
    try { await dispatch(fetchRecentMessages({ limit: 50 })); } catch {}
  }
};

// =========== EXPORTS ===========
export const {
  setConnected,
  setError,
  clearError,
  setActiveUsersCount,
  addMessage,
  addLocalMessage,
  confirmMessage,
  updateMessageLikes,
  updateMessageReactions,
  setTypingUser,
  updatePollResults,
  updateChatChallenge,
  markMessagesAsRead,
  updateUnreadCount,
  adjustUnreadCount,
  setLoungeInForeground,
  clearMessages,
  setMessages,
  removeMessage,
  updateMessageReports,
  setLastReadMessageId,
  setReadStateLoaded,
} = generalChatSlice.actions;

export const resetSocketState = () => {
  isInitialized = false;
  recentlyProcessedMessages.clear();
  processingMessages.clear();
  console.log('[Socket] Socket state reset - all tracking cleared');
};

export default generalChatSlice.reducer;
